/**
 * RETURNS — EMPTIES COME BACK (item 8).
 *
 * The customer's holdings are offered first, because what comes back is almost
 * always something he already has: a serial he does not hold is a question, not
 * a typing error to be accepted quietly.
 *
 * Each cylinder gets a CONDITION, and a damaged one gets a fault: the condition
 * is what the yard saw, and the fault is who pays for it. They are two
 * different judgements and this screen keeps them apart (D138/D165).
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Count, Loading, Pill, Register, ScanBox, Select, Serial, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { CustomerPickerById } from '../pickers.js';
import { NextStep } from '../next.js';
import { actionWords } from '../standing.js';
import { count, date, days, today, words, when } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('returns')!.messages;
type Condition = 'ok' | 'leaking' | 'valve_damaged' | 'body_damage' | 'not_accepted';

export function Returns({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <ReceiveEmpties />;
  if (id) return <OneReturn challanId={Number(id)} session={session} />;
  return <ReturnRegister session={session} />;
}

const CONDITIONS: readonly { value: Condition; label: string }[] = [
  { value: 'ok', label: 'OK' }, { value: 'leaking', label: 'Leaking' }, { value: 'valve_damaged', label: 'Valve damaged' },
  { value: 'body_damage', label: 'Body damaged' }, { value: 'not_accepted', label: 'Not accepted' },
];

/**
 * ONE RETURN, BEFORE AND AFTER IT IS CHECKED.
 *
 * The condition a cylinder came back in is the yard's judgement, made at the
 * gate in a hurry, and it is wrong often enough that the man who checks the
 * return must be able to change it — while the return is still pending, and
 * never after approval, because approval is what puts the damage on somebody's
 * account. The fault (who pays) is decided at approval, alongside it.
 */
function OneReturn({ challanId, session }: { challanId: number; session: Session }) {
  const asked = useAsk(() => api.view('challans.get', { challanId }), [challanId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [faults, setFaults] = useState<Record<number, string>>({});
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  if (asked.loading) return <Page title="Return"><Loading what="the return" /></Page>;
  if (asked.error) return <Page title="Return"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const c = d.challan;
  const pending = c.status === 'pending';
  const damaged = d.lines.filter((l) => l.inspectionOutcome && l.inspectionOutcome !== 'ok');

  async function correct(cylinderId: number, outcome: Condition) {
    const ok = await runner.run(() => api.command('challan.editPendingReturn', { challanId, cylinderId, outcome }));
    if (ok) asked.reload();
  }
  async function approve() {
    const chosen = Object.entries(faults).filter(([, v]) => v);
    const ok = await runner.run((retryKey) => api.command('challan.approve', {
      challanId,
      ...(chosen.length ? { faults: Object.fromEntries(chosen) as Record<string, 'company' | 'customer' | 'none'> } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) asked.reload();
  }
  async function reject() {
    const ok = await runner.run(() => api.command('challan.reject', { challanId, reason: reason.trim() }));
    if (ok) { setRejecting(false); asked.reload(); }
  }

  return (
    <Page title={c.number ? `Return ${c.number}` : `Empties back from ${c.customerName}`}
          sub={`${c.customerName} · ${c.siteName} · ${date(c.eventDate)}`}
          actions={pending ? <>
            {can(session, 'challan.approve') ? <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve the return'}</Button> : null}
            {can(session, 'challan.approve') ? <Button kind="danger" onClick={() => setRejecting((v) => !v)}>{rejecting ? 'Keep it' : 'Reject'}</Button> : null}
          </> : <Pill>{c.status}</Pill>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {pending ? (
        <NextStep title="Not approved yet — the cylinders are still with the customer until this is approved."
                  detail="Check the condition of each cylinder below; approving puts them back in the plant and settles who pays for any damage." />
      ) : c.status === 'approved' ? (
        <NextStep kind="good" title={`Return approved — ${count(d.lines.length)} cylinder${d.lines.length === 1 ? '' : 's'} back in the plant${damaged.length ? ` (${count(damaged.length)} damaged)` : ''}.`}
                  detail="The receipt challan is issued. The customer's holding has stopped for these cylinders."
                  secondary={{ label: "The customer's account", href: `#/account/${c.customerId}` }}
                  {...(can(session, 'challan.create') ? { action: { label: 'Record another return', href: '#/returns/new' } } : {})} />
      ) : c.status === 'rejected' ? (
        <NextStep kind="warn" title="Rejected — the cylinders are still with the customer." {...(c.rejectReason ? { detail: c.rejectReason } : {})} />
      ) : null}

      {rejecting ? (
        <Card title="Reject this return" sub="say what is wrong with it; the cylinders stay where they are">
          <TextArea name="reject-reason" label="Why it is rejected" value={reason} onChange={setReason} />
          <Button kind="danger" onClick={() => { void reject(); }} disabled={runner.busy || !reason.trim()}>Reject the return</Button>
        </Card>
      ) : null}


      <Card title="Papers" sub="printed when the return is approved, and never redrawn">
        {d.documents.length === 0 ? <p className="muted">No document has been issued for this return yet — the receipt challan is issued when it is approved.</p> : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {d.documents.map((doc) => (
              <li key={doc.id}>
                <a href={api.documentUrl(doc.id)} target="_blank" rel="noreferrer">{words(doc.kind)} {doc.number}</a>
                <span className="muted"> · version {doc.version}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="What came back" sub={pending ? 'the condition can still be corrected — the fault is settled when it is approved' : 'as approved'}>
        <Register
          columns={[
            { key: 'cyl', head: 'Cylinder', cell: (l: { internalId: string }) => <Serial>{l.internalId}</Serial> },
            { key: 'serial', head: 'Serial', cell: (l: { serialNo: string }) => l.serialNo },
            { key: 'gas', head: 'Gas', cell: (l: { gasService: string }) => words(l.gasService) },
            { key: 'condition', head: 'Condition', cell: (l: { cylinderId: number; inspectionOutcome: string | null }) => pending && can(session, 'challan.edit_pending')
                ? <Select name={`condition-${l.cylinderId}`} label="" value={(l.inspectionOutcome ?? 'ok')}
                          onChange={(v) => { void correct(l.cylinderId, v as Condition); }}
                          options={CONDITIONS.map((x) => ({ value: x.value, label: x.label }))} />
                : <Pill tone={l.inspectionOutcome === 'ok' ? 'good' : 'danger'}>{l.inspectionOutcome ?? 'not inspected'}</Pill> },
            { key: 'now', head: 'Now', cell: (l: { currentState: string | null }) => <Pill>{l.currentState}</Pill> },
          ]}
          rows={d.lines} empty="Nothing is on this return." />
      </Card>

      {pending && damaged.length ? (
        <Card title="Who pays for the damage" sub="decide now, or leave it to decide later">
          {damaged.map((l) => (
            <div className="queue-item" key={l.cylinderId}>
              <span className="grow"><Serial>{l.internalId}</Serial> <span className="muted">{words(l.inspectionOutcome)}</span></span>
              <Select name={`fault-${l.cylinderId}`} label="" value={faults[l.cylinderId] ?? ''} placeholder="Decide later"
                      onChange={(v) => setFaults((old) => ({ ...old, [l.cylinderId]: v }))}
                      options={[{ value: 'customer', label: "The customer's" }, { value: 'company', label: 'Ours' }, { value: 'none', label: 'Nobody — fair wear' }]} />
            </div>
          ))}
        </Card>
      ) : null}

      <Card title="What happened to it" sub="who did what, when, and why">
        <ul className="timeline">
          {d.audit.map((a) => (
            <li key={a.auditId}>
              <strong>{actionWords(a.action)}</strong> by {a.actorName ?? `user ${a.actorId}`}
              {a.reason ? <> — {a.reason}</> : null}
              <div className="when">{when(a.at)}</div>
            </li>
          ))}
        </ul>
      </Card>
    </Page>
  );
}

function ReturnRegister({ session }: { session: Session }) {
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | ''>('');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('challans.list', { type: 'return', ...(status ? { status } : {}), page, pageSize: 50 }), [status, page], MESSAGES);
  const columns: readonly Column<{ challanId: number; number: string | null; customerName: string; siteName: string; eventDate: string; lineCount: number; status: string }>[] = [
    { key: 'number', head: 'Return challan', cell: (r) => r.number ? <Serial>{r.number}</Serial> : <span className="muted">not issued</span> },
    { key: 'customer', head: 'Customer', cell: (r) => <><strong>{r.customerName}</strong><div className="muted">{r.siteName}</div></> },
    { key: 'date', head: 'Date', cell: (r) => date(r.eventDate) },
    { key: 'cyl', head: 'Cylinders', right: true, cell: (r) => <Count n={r.lineCount} /> },
    { key: 'status', head: 'Status', cell: (r) => <Pill>{r.status}</Pill> },
  ];
  return (
    <Page title="Returns" sub="Empties coming back, and what condition they came back in."
          actions={can(session, 'challan.create') ? <Button kind="primary" onClick={() => go('#/returns/new')}>Record a return</Button> : null}>
      <div className="toolbar">
        <Select name="status" label="Status" value={status} onChange={(v) => { setStatus(v as 'pending' | 'approved' | 'rejected' | ''); setPage(1); }} placeholder="Any status"
                options={[{ value: 'pending', label: 'Waiting to be checked' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }]} />
      </div>
      <Card>
        {asked.loading ? <Loading what="returns" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No return matches that."
                        onOpen={(r) => go(`#/returns/${r.challanId}`)} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

interface Coming { cylinderId: number; internalId: string; serialNo: string; gasService: string; daysOut: number; outcome: Condition; fault: string }

function ReceiveEmpties() {
  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [eventDate, setEventDate] = useState(today());
  const [vehicle, setVehicle] = useState('');
  const [serial, setSerial] = useState('');
  const [queue, setQueue] = useState<Coming[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const runner = useRun(MESSAGES);

  const sites = useAsk(() => (customerId ? api.view('sites.list', { customerId: Number(customerId), active: true }) : Promise.resolve({ rows: [] })), [customerId], MESSAGES);
  // Paged, and the SCAN no longer depends on the page (audit, 9 Oct 2026):
  // this loaded 200 holdings and looked the typed serial up in them, so a
  // cylinder past the 200th was told "this customer is not holding it".
  const [holdPage, setHoldPage] = useState(1);
  const holdings = useAsk(() => (customerId ? api.view('account.holdings', { customerId: Number(customerId), page: holdPage, pageSize: 50 })
                                            : Promise.resolve(null)), [customerId, holdPage], MESSAGES);
  /** more than one of this customer's cylinders answers to the typed serial — the operator picks */
  const [choices, setChoices] = useState<{ typed: string; rows: Coming[] }[]>([]);

  function add(h: { cylinderId: number; internalId: string; serialNo: string; gasService: string; daysOut: number }) {
    if (queue.some((q) => q.cylinderId === h.cylinderId)) { setScanError(`${h.internalId} is already on this return.`); return; }
    setScanError(null);
    setQueue((old) => [...old, { ...h, outcome: 'ok', fault: '' }]);
  }

  /**
   * EXACT IDENTITY, THE SAME WAY DISPATCH DOES IT (audit, 9 Oct 2026). This
   * took the FIRST holding whose number or serial matched. A serial is not
   * unique in this fleet — the same sticker number sits on more than one
   * shell — so a customer holding two could have the wrong trip returned and
   * its rent stopped. The server resolves the token to every cylinder it
   * names; only the ones THIS customer holds count; one is taken, none is said
   * in words, and more than one is put to the operator to choose.
   */
  async function addScanned() {
    const typed = serial.trim();
    if (!typed) return;
    try {
      const r = await api.view('cylinders.resolve', { token: typed });
      const mine = r.candidates.filter((c) => c.holder === `customer:${customerId}`);
      if (mine.length === 0) {
        setScanError(r.total === 0
          ? `This customer is not holding “${typed}” — no cylinder answers to that serial. Check it.`
          : `This customer is not holding “${typed}” — it is out with somebody else, or in the plant. Check the serial.`);
        return;
      }
      const asComing = (c: typeof mine[number]): Coming => ({
        cylinderId: c.cylinderId, internalId: c.internalId, serialNo: c.serialNo, gasService: c.gasService,
        daysOut: c.since ? Math.max(0, Math.round((Date.parse(today()) - Date.parse(c.since)) / 86_400_000)) : 0,
        outcome: 'ok', fault: '' });
      if (mine.length === 1) { add(asComing(mine[0]!)); setSerial(''); return; }
      setScanError(null);
      setChoices((old) => [...old.filter((x) => x.typed !== typed), { typed, rows: mine.map(asComing) }]);
      setSerial('');
    } catch (e) {
      setScanError(e instanceof Error ? e.message : String(e));
    }
  }

  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('challan.createReturn', {
        customerId: Number(customerId), siteId: Number(siteId), eventDate,
        ...(vehicle.trim() ? { vehicle: vehicle.trim() } : {}),
        lines: queue.map((q) => ({
          cylinderId: q.cylinderId, outcome: q.outcome,
          ...(q.outcome !== 'ok' && q.fault ? { fault: q.fault as 'company' | 'customer' | 'none' } : {}),
        })),
      });
      const value = r.value as { challanId?: number };
      if (value?.challanId) go(`#/returns/${value.challanId}`);
      return r;
    });
    if (!ok) return;
  }

  return (
    <Page title="Record a return" sub="What came back, and in what condition. Nothing closes until it is approved.">
      <div className="grid two">
        <Card title="The return">
          {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
          <CustomerPickerById name="customer" label="Which customer?" customerId={customerId}
                              onPick={(v) => { setCustomerId(v); setSiteId(''); setQueue([]); }} placeholder="Search or select customer…" />
          <Select name="site" label="Site" value={siteId} onChange={setSiteId} placeholder={customerId ? 'Choose a site' : 'Choose the customer first'}
                  options={(sites.data?.rows ?? []).map((s) => ({ value: String(s.siteId), label: s.name + (s.isPrimary ? ' (primary)' : '') }))} />
          <Text name="event-date" label="Date" type="date" value={eventDate} onChange={setEventDate} required />
          <Text name="vehicle" label="Truck / vehicle no (optional)" value={vehicle} onChange={setVehicle} />

          {scanError ? <Banner kind="warn" title={scanError} /> : null}
          {choices.map((ch) => (
            <div key={ch.typed}>
              <Banner kind="warn" title={`“${ch.typed}” is on ${count(ch.rows.length)} of this customer's cylinders — which one came back?`} />
              <div className="toolbar">
                {ch.rows.map((c) => (
                  <Button key={c.cylinderId} small onClick={() => { add(c); setChoices((old) => old.filter((x) => x.typed !== ch.typed)); }}>
                    {c.internalId} · {words(c.gasService)} · {count(c.daysOut)} days out
                  </Button>
                ))}
              </div>
            </div>
          ))}
          <ScanBox label="Cylinder serial" value={serial} onChange={setSerial} onAdd={() => { void addScanned(); }}
                   help="only cylinders this customer is holding can come back on this return" />

          <Button kind="primary" wide disabled={!customerId || !siteId || queue.length === 0 || runner.busy} onClick={() => { void submit(); }}>
            {runner.busy ? 'Recording…' : queue.length === 0 ? 'Add cylinders first' : `Record the return (${count(queue.length)})`}
          </Button>
        </Card>

        <div>
          <Card title="Coming back" {...(queue.length ? { sub: `${count(queue.length)} cylinder(s)` } : {})}>
            {queue.length === 0 ? (
              <div className="queue empty">
                <strong>Nothing added yet</strong>
                <span>Scan a serial, or pick from what this customer is holding below.</span>
              </div>
            ) : queue.map((q) => (
              <div key={q.cylinderId} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <Serial>{q.internalId}</Serial>
                  <span className="muted">{q.serialNo} · {words(q.gasService)} · out {days(q.daysOut)}</span>
                  <Button small kind="danger" onClick={() => setQueue((old) => old.filter((x) => x.cylinderId !== q.cylinderId))}>Remove</Button>
                </div>
                <Select name={`cond-${q.cylinderId}`} label="Condition" value={q.outcome}
                        onChange={(v) => setQueue((old) => old.map((x) => (x.cylinderId === q.cylinderId ? { ...x, outcome: v as Condition } : x)))}
                        options={[{ value: 'ok', label: 'OK' }, { value: 'leaking', label: 'Leaking' }, { value: 'valve_damaged', label: 'Valve damaged' },
                                  { value: 'body_damage', label: 'Body damaged' }, { value: 'not_accepted', label: 'Not accepted' }]} />
                {q.outcome !== 'ok' ? (
                  <Select name={`fault-${q.cylinderId}`} label="Whose fault" value={q.fault}
                          onChange={(v) => setQueue((old) => old.map((x) => (x.cylinderId === q.cylinderId ? { ...x, fault: v } : x)))}
                          placeholder="Leave for the approver to decide"
                          options={[{ value: 'customer', label: "The customer's" }, { value: 'company', label: 'Ours' }, { value: 'none', label: 'Nobody — fair wear' }]} />
                ) : null}
              </div>
            ))}
          </Card>

          {customerId ? (
            <Card title="What this customer is holding" {...(holdings.data ? { sub: `${count(holdings.data.summary.cylindersOut)} out` } : {})}>
              {holdings.loading ? <Loading what="holdings" /> : (
                <Register
                  columns={[
                    { key: 'cyl', head: 'Cylinder', cell: (h: { internalId: string }) => <Serial>{h.internalId}</Serial> },
                    { key: 'gas', head: 'Gas', cell: (h: { gasService: string }) => words(h.gasService) },
                    { key: 'out', head: 'Out since', cell: (h: { outDate: string }) => date(h.outDate) },
                    { key: 'days', head: 'Days', right: true, cell: (h: { daysOut: number }) => count(h.daysOut) },
                    { key: 'add', head: '', cell: (h: { cylinderId: number; internalId: string; serialNo: string; gasService: string; daysOut: number }) =>
                        <Button small onClick={() => add(h)}>Add</Button> },
                  ]}
                  rows={holdings.data?.rows ?? []} empty="This customer is holding nothing." />
              )}
              {holdings.data ? <Pager page={holdings.data.page} onPage={setHoldPage} /> : null}
            </Card>
          ) : null}
        </div>
      </div>
    </Page>
  );
}
